import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, router } from '@inertiajs/react'
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  Eye,
  Edit,
  Trash2,
  Layout,
  CheckCircle,
  Clock,
  FileCode
} from 'lucide-react'
import { useState, useCallback } from 'react'

interface AdminPageContent {
  id: number
  page_key: string
  name: string
  description: string | null
  content_type: string
  community_name: string | null
  published: boolean
  published_at: string | null
  updated_at: string
  display_on_all_pages: boolean
  target_pages: string[]
  targeting_label: string
}

interface Pagination {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface Filters {
  search: string | null
  community_id: string | null
  content_type: string | null
  status: string | null
  sort: string | null
  direction: string | null
}

interface Community {
  id: number
  name: string
}

interface ContentType {
  value: string
  label: string
}

interface Stats {
  total: number
  published: number
  draft: number
}

interface IndexProps {
  page_contents: AdminPageContent[]
  pagination: Pagination
  filters: Filters
  communities: Community[]
  content_types: ContentType[]
  stats: Stats
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  })
}

const contentTypeLabels: Record<string, string> = {
  banner: 'Bannière',
  footer: 'Footer',
  events: 'Événements'
}

const contentTypeColors: Record<string, string> = {
  banner: 'bg-blue-100 text-blue-800',
  footer: 'bg-purple-100 text-purple-800',
  events: 'bg-green-100 text-green-800'
}

export default function Index({ page_contents, pagination, filters, communities, content_types, stats }: IndexProps) {
  const [search, setSearch] = useState(filters.search || '')

  const applyFilters = useCallback((newFilters: Partial<Filters>) => {
    router.get('/admin/page_contents', {
      ...filters,
      ...newFilters,
      page: 1
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleSearch = useCallback(() => {
    applyFilters({ search: search || null })
  }, [search, applyFilters])

  const handleSort = useCallback((field: string) => {
    const newDirection = filters.sort === field && filters.direction === 'desc' ? 'asc' : 'desc'
    applyFilters({ sort: field, direction: newDirection })
  }, [filters, applyFilters])

  const handlePageChange = useCallback((page: number) => {
    router.get('/admin/page_contents', {
      ...filters,
      page
    }, {
      preserveState: true,
      preserveScroll: true
    })
  }, [filters])

  const handleDelete = useCallback((pageContent: AdminPageContent) => {
    if (confirm(`Voulez-vous vraiment supprimer "${pageContent.name}" ?`)) {
      router.delete(`/admin/page_contents/${pageContent.id}`)
    }
  }, [])

  return (
    <AdminLayout
      title="Contenus editables"
      breadcrumbs={[{ label: 'Contenus editables' }]}
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Layout className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-dark">{stats.total}</p>
              <p className="text-sm text-text-gray">Total</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-dark">{stats.published}</p>
              <p className="text-sm text-text-gray">Publies</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-text-dark">{stats.draft}</p>
              <p className="text-sm text-text-gray">Brouillons</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Actions Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
        <div className="flex flex-1 gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Rechercher par nom ou cle..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} variant="secondary">
                <Search size={18} />
              </Button>
            </div>
          </div>
        </div>

        <Link href="/admin/page_contents/new">
          <Button className="flex items-center gap-2">
            <Plus size={18} />
            Nouveau contenu
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="w-48">
            <Select
              value={filters.community_id || ''}
              onChange={(e) => applyFilters({ community_id: e.target.value || null })}
            >
              <option value="">Toutes communautes</option>
              {communities.map((community) => (
                <option key={community.id} value={community.id}>
                  {community.name}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.content_type || ''}
              onChange={(e) => applyFilters({ content_type: e.target.value || null })}
            >
              <option value="">Tous types</option>
              {content_types.map((type) => (
                <option key={type.value} value={type.value}>
                  {contentTypeLabels[type.value] || type.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={filters.status || ''}
              onChange={(e) => applyFilters({ status: e.target.value || null })}
            >
              <option value="">Tous statuts</option>
              <option value="published">Publies</option>
              <option value="draft">Brouillons</option>
            </Select>
          </div>

          {(filters.search || filters.community_id || filters.content_type || filters.status) && (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch('')
                applyFilters({
                  search: null,
                  community_id: null,
                  content_type: null,
                  status: null
                })
              }}
            >
              Reinitialiser
            </Button>
          )}
        </div>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-text-gray">
          {pagination.total_count} contenu{pagination.total_count > 1 ? 's' : ''}
          {pagination.total_pages > 1 && (
            <span> - Page {pagination.current_page} sur {pagination.total_pages}</span>
          )}
        </p>
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('name')}
                  >
                    Nom
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('content_type')}
                  >
                    Type
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Pages
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Communaute
                </th>
                <th className="text-left px-4 py-3 font-medium text-text-gray">
                  Statut
                </th>
                <th className="text-left px-4 py-3">
                  <button
                    className="flex items-center gap-1 font-medium text-text-gray hover:text-text-dark"
                    onClick={() => handleSort('updated_at')}
                  >
                    Modifie
                    <ArrowUpDown size={14} />
                  </button>
                </th>
                <th className="text-right px-4 py-3 font-medium text-text-gray">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {page_contents.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text-gray">
                    <div className="flex flex-col items-center gap-2">
                      <FileCode size={32} className="text-gray-300" />
                      Aucun contenu editable trouve
                    </div>
                  </td>
                </tr>
              ) : (
                page_contents.map((pageContent) => (
                  <tr key={pageContent.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-text-dark">{pageContent.name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${contentTypeColors[pageContent.content_type] || 'bg-gray-100 text-gray-800'}`}>
                        {contentTypeLabels[pageContent.content_type] || pageContent.content_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${pageContent.display_on_all_pages ? 'text-tessan-green font-medium' : 'text-text-gray'}`}>
                        {pageContent.targeting_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-text-dark">
                        {pageContent.community_name || 'Global'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {pageContent.published ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                          <CheckCircle size={12} />
                          Publie
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-800">
                          <Clock size={12} />
                          Brouillon
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-gray">
                      {formatDate(pageContent.updated_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Link href={`/admin/page_contents/${pageContent.id}`}>
                          <Button variant="secondary" className="p-2">
                            <Eye size={16} />
                          </Button>
                        </Link>
                        <Link href={`/admin/page_contents/${pageContent.id}/edit`}>
                          <Button variant="secondary" className="p-2">
                            <Edit size={16} />
                          </Button>
                        </Link>
                        <Button
                          variant="secondary"
                          className="p-2 text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(pageContent)}
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <Button
              variant="secondary"
              disabled={pagination.current_page === 1}
              onClick={() => handlePageChange(pagination.current_page - 1)}
              className="flex items-center gap-1"
            >
              <ChevronLeft size={16} />
              Precedent
            </Button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1
                } else if (pagination.current_page <= 3) {
                  pageNum = i + 1
                } else if (pagination.current_page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i
                } else {
                  pageNum = pagination.current_page - 2 + i
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-8 h-8 rounded text-sm font-medium transition-colors ${
                      pagination.current_page === pageNum
                        ? 'bg-tessan-primary text-white'
                        : 'bg-white text-text-gray hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                )
              })}
            </div>

            <Button
              variant="secondary"
              disabled={pagination.current_page === pagination.total_pages}
              onClick={() => handlePageChange(pagination.current_page + 1)}
              className="flex items-center gap-1"
            >
              Suivant
              <ChevronRight size={16} />
            </Button>
          </div>
        )}
      </Card>
    </AdminLayout>
  )
}
